// src/views/dashboard/DashboardFinanceiro.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../services/api";

const fmt = v => Number(v||0).toLocaleString("pt-BR",{style:"currency",currency:"BRL"});
const fmtMes = m => { if(!m) return ""; const [y,mo]=m.split("-"); const meses=["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"]; return `${meses[parseInt(mo)-1]}/${y.slice(2)}`; };

// Simple bar chart using SVG
function BarChart({ recMeses, desMeses }) {
  const meses = [...new Set([...recMeses.map(r=>r.mes), ...desMeses.map(d=>d.mes)])].sort();
  const recMap = Object.fromEntries(recMeses.map(r=>[r.mes,r.total]));
  const desMap = Object.fromEntries(desMeses.map(d=>[d.mes,d.total]));
  const maxVal = Math.max(...meses.map(m=>Math.max(recMap[m]||0,desMap[m]||0)),1);
  const W=520, H=180, PAD=40, barW=28, gap=16;
  const x0 = PAD;
  const slotW = meses.length > 0 ? (W-PAD*2) / meses.length : 60;

  return (
    <svg viewBox={`0 0 ${W} ${H+30}`} style={{width:"100%",maxWidth:W}}>
      {/* Grid lines */}
      {[0,0.25,0.5,0.75,1].map(t=>(
        <g key={t}>
          <line x1={PAD} x2={W-PAD} y1={PAD+(1-t)*(H-PAD)} y2={PAD+(1-t)*(H-PAD)}
            stroke="#E2E8F0" strokeDasharray="4"/>
          <text x={PAD-4} y={PAD+(1-t)*(H-PAD)+4} textAnchor="end" fontSize="9" fill="#94A3B8">
            {fmt(maxVal*t).replace("R$","").trim()}
          </text>
        </g>
      ))}
      {/* Bars */}
      {meses.map((m,i)=>{
        const cx = x0 + i*slotW + slotW/2;
        const hR = ((recMap[m]||0)/maxVal)*(H-PAD);
        const hD = ((desMap[m]||0)/maxVal)*(H-PAD);
        return (
          <g key={m}>
            <rect x={cx-barW-2} y={H+PAD-hR} width={barW} height={hR} fill="#1B3A6B" rx="3" opacity="0.85"/>
            <rect x={cx+2}      y={H+PAD-hD} width={barW} height={hD} fill="#DC2626" rx="3" opacity="0.75"/>
            <text x={cx} y={H+PAD+14} textAnchor="middle" fontSize="9" fill="#64748B">{fmtMes(m)}</text>
          </g>
        );
      })}
      {/* Legend */}
      <rect x={PAD} y={H+PAD+22} width={10} height={10} fill="#1B3A6B" rx="2"/>
      <text x={PAD+14} y={H+PAD+31} fontSize="9" fill="#475569">Receitas</text>
      <rect x={PAD+70} y={H+PAD+22} width={10} height={10} fill="#DC2626" rx="2"/>
      <text x={PAD+84} y={H+PAD+31} fontSize="9" fill="#475569">Despesas</text>
    </svg>
  );
}

// Pie chart for despesas por categoria
function PieChart({ categorias }) {
  if (!categorias?.length) return <div style={{textAlign:"center",color:"var(--gray-400)",padding:20}}>Sem dados</div>;
  const COLORS = ["#1B3A6B","#2563EB","#059669","#D97706","#DC2626","#7C3AED","#0891B2","#475569"];
  const total = categorias.reduce((s,c)=>s+Number(c.total),0);
  const R=70, CX=90, CY=80;
  let angle = -Math.PI/2;
  const slices = categorias.slice(0,8).map((c,i)=>{
    const val = Number(c.total) || 0;
    const pct = val / total;
    const a1=angle, a2=angle+pct*2*Math.PI;
    angle=a2;
    const x1=CX+R*Math.cos(a1), y1=CY+R*Math.sin(a1);
    const x2=CX+R*Math.cos(a2), y2=CY+R*Math.sin(a2);
    const large=pct>0.5?1:0;
    const label = c.categoria || "Sem classificação";
    return {path:`M${CX},${CY} L${x1},${y1} A${R},${R},0,${large},1,${x2},${y2} Z`,
      color:COLORS[i%COLORS.length], label, pct, val, qtd: Number(c.qtd) || 0};
  });
  return (
    <svg viewBox="0 0 300 200" style={{width:"100%",maxWidth:300}}>
      {slices.map((s,i)=><path key={i} d={s.path} fill={s.color} stroke="#fff" strokeWidth="2"/>)}
      {slices.map((s,i)=>(
        <g key={`leg-${i}`}>
          <rect x={168} y={8+i*22} width={10} height={10} fill={s.color} rx="2"/>
          <text x={182} y={16+i*22} fontSize="8.5" fill="#374151" fontWeight="600">
            {s.label.length>18?s.label.substring(0,17)+"…":s.label}
          </text>
          <text x={182} y={26+i*22} fontSize="8" fill="#64748B">
            {fmt(s.val)} · {(s.pct*100).toFixed(1)}%
            {s.qtd > 0 ? ` · ${s.qtd} lanç.` : ""}
          </text>
        </g>
      ))}
    </svg>
  );
}

// Line chart for inadimplência
function LineChart({ inadMeses }) {
  const W=520, H=140, PAD=40;
  if (!inadMeses?.length) return <div style={{textAlign:"center",color:"var(--gray-400)",padding:20}}>Sem dados</div>;
  const maxQtd = Math.max(...inadMeses.map(m=>m.qtd),1);
  const pts = inadMeses.map((m,i)=>{
    const x = PAD + i*(W-PAD*2)/(Math.max(inadMeses.length-1,1));
    const y = PAD + (1-m.qtd/maxQtd)*(H-PAD);
    return {x,y,m};
  });
  const d = pts.map((p,i)=>`${i===0?"M":"L"}${p.x},${p.y}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H+20}`} style={{width:"100%",maxWidth:W}}>
      {[0,0.5,1].map(t=>(
        <g key={t}>
          <line x1={PAD} x2={W-PAD} y1={PAD+(1-t)*(H-PAD)} y2={PAD+(1-t)*(H-PAD)}
            stroke="#E2E8F0" strokeDasharray="4"/>
          <text x={PAD-4} y={PAD+(1-t)*(H-PAD)+4} textAnchor="end" fontSize="9" fill="#94A3B8">
            {Math.round(maxQtd*t)}
          </text>
        </g>
      ))}
      <path d={d} fill="none" stroke="#DC2626" strokeWidth="2.5"/>
      {pts.map((p,i)=>(
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="4" fill="#DC2626" stroke="#fff" strokeWidth="2"/>
          <text x={p.x} y={H+PAD+10} textAnchor="middle" fontSize="9" fill="#64748B">{fmtMes(p.m.mes)}</text>
        </g>
      ))}
    </svg>
  );
}

// Helpers para presets de período (mês atual, trimestre atual, ano atual e mês passado).
function periodoPreset(p) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  const fmt = d => d.toISOString().split("T")[0];
  if (p === "mes")       return { dtIni: fmt(new Date(y,m,1)),   dtFim: fmt(new Date(y,m+1,0)) };
  if (p === "mesAnt")    return { dtIni: fmt(new Date(y,m-1,1)), dtFim: fmt(new Date(y,m,0)) };
  if (p === "trim")      { const tIni=Math.floor(m/3)*3; return { dtIni: fmt(new Date(y,tIni,1)), dtFim: fmt(new Date(y,tIni+3,0)) }; }
  if (p === "ano")       return { dtIni: fmt(new Date(y,0,1)),   dtFim: fmt(new Date(y,11,31)) };
  if (p === "12meses")   return { dtIni: fmt(new Date(y-1,m,1)), dtFim: fmt(new Date(y,m+1,0)) };
  return { dtIni:"", dtFim:"" };
}
const fmtDateBR = s => { try { return new Date(s+"T12:00:00").toLocaleDateString("pt-BR"); } catch { return s; } };

export default function DashboardFinanceiro() {
  const { condominioAtivo, condominios } = useAuth();
  const [idCondo, setIdCondo] = useState("");
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [preset, setPreset]   = useState("mes");
  const [{dtIni, dtFim}, setRange] = useState(periodoPreset("mes"));
  const [lastUpdate, setLastUpdate] = useState(null);

  // Set condo from context
  useEffect(()=>{
    const id = condominioAtivo?.id || condominios?.[0]?.id || "";
    if (id) setIdCondo(String(id));
  }, [condominioAtivo, condominios]);

  const carregar = useCallback(async () => {
    if (!idCondo) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({ idCondominio:idCondo });
      if (dtIni) qs.set("dtIni", dtIni);
      if (dtFim) qs.set("dtFim", dtFim);
      const r = await api("GET", `/relatorios/dashboard-financeiro?${qs.toString()}`);
      if (r.success) {
        setData(r.data);
        setLastUpdate(new Date().toLocaleTimeString("pt-BR"));
      }
    } catch(e){ console.error(e); }
    setLoading(false);
  }, [idCondo, dtIni, dtFim]);

  useEffect(()=>{ carregar(); }, [carregar]);

  // Auto-refresh a cada 5 minutos (alinhado com o Dashboard Principal)
  useEffect(() => {
    const t = setInterval(carregar, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [carregar]);

  const aplicarPreset = (p) => { setPreset(p); setRange(periodoPreset(p)); };
  const condoNome = condominioAtivo?.nmFantasia || condominioAtivo?.nmCondominio || "";

  const cards = data?.cards || {};

  return (
    <div style={{padding:"24px",background:"var(--bg-surface-alt)",minHeight:"100vh"}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:12}}>
        <div>
          <h1 style={{fontSize:22,fontWeight:800,color:"#1B3A6B",margin:0}}>
            <i className="bi bi-graph-up-arrow me-2" style={{color:"#2563EB"}}/>
            Dashboard Financeiro
          </h1>
          <p style={{fontSize:13,color:"var(--gray-500)",margin:"4px 0 0"}}>
            {condoNome ? <><strong>{condoNome}</strong> — </> : ""}
            Período: {fmtDateBR(dtIni)} a {fmtDateBR(dtFim)}
            {lastUpdate && <span style={{marginLeft:10,fontSize:11,color:"var(--gray-400)"}}>
              <i className="bi bi-clock me-1"/>Atualizado às {lastUpdate}
            </span>}
          </p>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
          {condominios?.length > 1 && (
            <select className="form-select form-select-sm" style={{width:220}}
              value={idCondo} onChange={e=>setIdCondo(e.target.value)}>
              {condominios.map(c=><option key={c.id} value={c.id}>{c.nmFantasia||c.nmCondominio}</option>)}
            </select>
          )}
          <button className="btn btn-outline-secondary btn-sm" onClick={carregar} disabled={loading} title="Atualizar">
            <i className={`bi bi-arrow-clockwise ${loading?"spin":""}`}/>
          </button>
          <button className="btn btn-outline-secondary btn-sm" onClick={()=>window.print()} title="Imprimir">
            <i className="bi bi-printer"/>
          </button>
        </div>
      </div>

      {/* Filtro de período */}
      <div className="no-print" style={{display:"flex",gap:8,alignItems:"center",marginBottom:20,flexWrap:"wrap",
        background:"var(--bg-surface)",padding:"10px 14px",borderRadius:10,boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}>
        {[
          {v:"mes",    l:"Mês atual"},
          {v:"mesAnt", l:"Mês anterior"},
          {v:"trim",   l:"Trimestre"},
          {v:"ano",    l:"Ano"},
          {v:"12meses",l:"Últimos 12 meses"},
        ].map(p=>(
          <button key={p.v} type="button" onClick={()=>aplicarPreset(p.v)}
            style={{padding:"5px 12px",borderRadius:6,border:"1px solid var(--border-color)",fontSize:12,
              background:preset===p.v?"#1B3A6B":"#fff", color:preset===p.v?"#fff":"#475569",
              fontWeight:600,cursor:"pointer"}}>
            {p.l}
          </button>
        ))}
        <span style={{fontSize:11,color:"var(--gray-400)",marginLeft:4}}>ou</span>
        <input type="date" className="form-control form-control-sm" style={{width:140}}
          value={dtIni} onChange={e=>{setPreset("custom");setRange(r=>({...r,dtIni:e.target.value}));}}/>
        <span style={{fontSize:12,color:"var(--gray-400)"}}>até</span>
        <input type="date" className="form-control form-control-sm" style={{width:140}}
          value={dtFim} onChange={e=>{setPreset("custom");setRange(r=>({...r,dtFim:e.target.value}));}}/>
      </div>

      {loading ? (
        <div style={{textAlign:"center",padding:60}}>
          <div className="spinner-border text-primary"/>
          <p style={{marginTop:12,color:"var(--gray-500)"}}>Carregando dados financeiros...</p>
        </div>
      ) : !data ? (
        <div style={{textAlign:"center",padding:60,color:"var(--gray-400)"}}>
          <i className="bi bi-graph-up" style={{fontSize:48,display:"block",marginBottom:12}}/>
          Selecione um condomínio para visualizar
        </div>
      ) : (
        <React.Fragment>
          {/* Cards */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:16,marginBottom:24}}>
            {[
              {label:"Receitas do Mês",    value:fmt(cards.recMes),   icon:"bi-arrow-down-circle-fill", color:"#059669", bg:"#D1FAE5"},
              {label:"A Receber (Vencido)",value:fmt(cards.aReceber), icon:"bi-clock-fill",              color:"#D97706", bg:"#FEF3C7"},
              {label:"Inadimplentes",      value:cards.qtdInad+" unid",icon:"bi-exclamation-circle-fill",color:"#DC2626", bg:"#FEE2E2"},
              {label:"Despesas do Mês",    value:fmt(cards.desMes),   icon:"bi-arrow-up-circle-fill",   color:"#2563EB", bg:"#DBEAFE"},
              {label:"Saldo Bancário",     value:fmt(cards.saldoBancario), icon:"bi-bank",               color:"#7C3AED", bg:"#EDE9FE"},
              {label:"Resultado do Mês",   value:fmt(cards.resultado),    icon:"bi-graph-up",             color:parseFloat(cards.resultado||0)>=0?"#059669":"#DC2626", bg:parseFloat(cards.resultado||0)>=0?"#D1FAE5":"#FEE2E2"},
            ].map((c,i)=>(
              <div key={i} style={{background:"var(--bg-surface)",borderRadius:12,padding:"18px 20px",
                boxShadow:"0 1px 4px rgba(0,0,0,.08)",borderLeft:`4px solid ${c.color}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div>
                    <div style={{fontSize:11,fontWeight:600,color:"var(--gray-400)",textTransform:"uppercase",letterSpacing:.5,marginBottom:6}}>
                      {c.label}
                    </div>
                    <div style={{fontSize:20,fontWeight:800,color:"var(--text-primary)"}}>{c.value}</div>
                  </div>
                  <div style={{width:42,height:42,borderRadius:10,background:c.bg,
                    display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <i className={`bi ${c.icon}`} style={{fontSize:20,color:c.color}}/>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Charts row 1 */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
            <div style={{background:"var(--bg-surface)",borderRadius:12,padding:"20px",boxShadow:"0 1px 4px rgba(0,0,0,.08)"}}>
              <h3 style={{fontSize:14,fontWeight:700,color:"#1B3A6B",marginBottom:16}}>
                <i className="bi bi-bar-chart-fill me-2"/>Receitas vs Despesas (6 meses)
              </h3>
              <BarChart recMeses={data.recMeses} desMeses={data.desMeses}/>
            </div>
            <div style={{background:"var(--bg-surface)",borderRadius:12,padding:"20px",boxShadow:"0 1px 4px rgba(0,0,0,.08)"}}>
              <h3 style={{fontSize:14,fontWeight:700,color:"#1B3A6B",marginBottom:4}}>
                <i className="bi bi-pie-chart-fill me-2"/>Despesas por grupo (plano de contas)
              </h3>
              <p style={{fontSize:11,color:"var(--gray-500)",marginBottom:12}}>
                Soma dos valores pagos no período filtrado, agrupados por grupo contábil (não por título de cada conta a pagar).
              </p>
              <PieChart categorias={data.desCategorias}/>
            </div>
          </div>

          {/* Charts row 2 */}
          <div style={{background:"var(--bg-surface)",borderRadius:12,padding:"20px",boxShadow:"0 1px 4px rgba(0,0,0,.08)",marginBottom:16}}>
            <h3 style={{fontSize:14,fontWeight:700,color:"#1B3A6B",marginBottom:16}}>
              <i className="bi bi-graph-down-arrow me-2" style={{color:"#DC2626"}}/>
              Evolução da Inadimplência (6 meses)
            </h3>
            <LineChart inadMeses={data.inadMeses}/>
          </div>

          {/* Top inadimplentes + Próximos vencimentos */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
            {/* Top 5 Inadimplentes */}
            <div style={{background:"var(--bg-surface)",borderRadius:12,padding:"20px",boxShadow:"0 1px 4px rgba(0,0,0,.08)"}}>
              <h3 style={{fontSize:14,fontWeight:700,color:"#1B3A6B",marginBottom:12}}>
                <i className="bi bi-exclamation-triangle-fill me-2" style={{color:"#DC2626"}}/>Top Inadimplentes
              </h3>
              {(data.topInad||[]).length===0 ? (
                <p style={{fontSize:13,color:"var(--gray-400)",textAlign:"center",padding:"20px 0"}}>Nenhum inadimplente</p>
              ) : (
                <table style={{width:"100%",fontSize:12,borderCollapse:"collapse"}}>
                  <thead><tr style={{borderBottom:"2px solid #F1F5F9"}}>
                    <th style={{padding:"6px 4px",textAlign:"left",color:"var(--gray-500)"}}>Unidade</th>
                    <th style={{padding:"6px 4px",textAlign:"left",color:"var(--gray-500)"}}>Condômino</th>
                    <th style={{padding:"6px 4px",textAlign:"right",color:"var(--gray-500)"}}>Em Aberto</th>
                  </tr></thead>
                  <tbody>
                    {(data.topInad||[]).map((u,i)=>(
                      <tr key={i} style={{borderBottom:"1px solid #F8FAFC"}}>
                        <td style={{padding:"6px 4px",fontWeight:600}}>
                          Un.{u.unidade}{u.bloco?`/Bl.${u.bloco}`:""}
                        </td>
                        <td style={{padding:"6px 4px",color:"var(--gray-600)",maxWidth:120,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.nmCondomino}</td>
                        <td style={{padding:"6px 4px",textAlign:"right",fontWeight:700,color:"#DC2626"}}>{fmt(u.vlrTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Próximos Vencimentos */}
            <div style={{background:"var(--bg-surface)",borderRadius:12,padding:"20px",boxShadow:"0 1px 4px rgba(0,0,0,.08)"}}>
              <h3 style={{fontSize:14,fontWeight:700,color:"#1B3A6B",marginBottom:12}}>
                <i className="bi bi-calendar-event me-2" style={{color:"#2563EB"}}/>Vencimentos (7 dias)
              </h3>
              {(data.proxVenc||[]).length===0 ? (
                <p style={{fontSize:13,color:"var(--gray-400)",textAlign:"center",padding:"20px 0"}}>Nenhum boleto a vencer</p>
              ) : (
                <table style={{width:"100%",fontSize:12,borderCollapse:"collapse"}}>
                  <thead><tr style={{borderBottom:"2px solid #F1F5F9"}}>
                    <th style={{padding:"6px 4px",textAlign:"left",color:"var(--gray-500)"}}>Vencimento</th>
                    <th style={{padding:"6px 4px",textAlign:"left",color:"var(--gray-500)"}}>Unidade</th>
                    <th style={{padding:"6px 4px",textAlign:"right",color:"var(--gray-500)"}}>Valor</th>
                  </tr></thead>
                  <tbody>
                    {(data.proxVenc||[]).map((b,i)=>(
                      <tr key={i} style={{borderBottom:"1px solid #F8FAFC"}}>
                        <td style={{padding:"6px 4px",fontFamily:"monospace",fontSize:11}}>
                          {new Date(b.dtVencimento+"T12:00:00").toLocaleDateString("pt-BR")}
                        </td>
                        <td style={{padding:"6px 4px",color:"var(--gray-600)"}}>
                          Un.{b.unidade}{b.bloco?`/Bl.${b.bloco}`:""}
                        </td>
                        <td style={{padding:"6px 4px",textAlign:"right",fontWeight:600,color:"#2563EB"}}>{fmt(b.vlrBoleto)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </React.Fragment>
      )}

      <style>{`
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        .spin { animation: spin 1s linear infinite; }
        @media print {
          .no-print { display:none !important; }
          body, .form-select { background:#fff !important; }
        }
      `}</style>
    </div>
  );
}
